import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Layout Components (always loaded)
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingFallback from './components/common/LoadingFallback';
import ErrorBoundary from './components/common/ErrorBoundary';
import CookieConsent from './components/legal/CookieConsent';

// Route Components (always loaded)
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';

// Public Pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const MatchesPage = React.lazy(() => import('./pages/MatchesPage'));
const MatchDetailPage = React.lazy(() => import('./pages/MatchDetailPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const AuctionsPage = React.lazy(() => import('./pages/AuctionsPage'));
const AuctionDetailPage = React.lazy(() => import('./pages/AuctionDetailPage'));
const AuctionCheckoutPage = React.lazy(() => import('./pages/AuctionCheckoutPage'));
const BuyerAccountPage = React.lazy(() => import('./pages/BuyerAccountPage'));
const TermsPage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.PrivacyPage })));
const CookiesPage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.CookiesPage })));
const LegalNoticePage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.LegalNoticePage })));
const HelpPage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.HelpPage })));
const AboutPage = React.lazy(() => import('./pages/legal/LegalPages').then(m => ({ default: m.AboutPage })));

// Protected Pages
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const RequestsPage = React.lazy(() => import('./pages/RequestsPage'));
const RequestDetailPage = React.lazy(() => import('./pages/RequestDetailPage'));
const CreateRequestPage = React.lazy(() => import('./pages/CreateRequestPage'));
const OffersPage = React.lazy(() => import('./pages/OffersPage'));
const RatingsPage = React.lazy(() => import('./pages/RatingsPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));

// Seller Pages
const SellerRequestsPage = React.lazy(() => import('./pages/SellerRequestsPage'));
const SellerOffersPage = React.lazy(() => import('./pages/SellerOffersPage'));
const SellerInventoryPage = React.lazy(() => import('./pages/SellerInventoryPage'));
const CreateOfferPage = React.lazy(() => import('./pages/CreateOfferPage'));
const CreateMatchPage = React.lazy(() => import('./pages/CreateMatchPage'));

// Payment Pages
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const PaymentConfirmationPage = React.lazy(() => import('./pages/PaymentConfirmationPage'));
const PaymentHistoryPage = React.lazy(() => import('./pages/PaymentHistoryPage'));
const SellerStripeOnboardingPage = React.lazy(() => import('./pages/SellerStripeOnboardingPage'));
const ActivityPage = React.lazy(() => import('./pages/ActivityPage'));
const FavoritesPage = React.lazy(() => import('./pages/FavoritesPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const SellerPublicProfilePage = React.lazy(() => import('./pages/SellerPublicProfilePage'));
const SellerApplyPage = React.lazy(() => import('./pages/SellerApplyPage'));
const SellerSalesPage = React.lazy(() => import('./pages/SellerSalesPage'));

// Admin Pages
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminMatchesPage = React.lazy(() => import('./pages/AdminMatchesPage'));
const AdminSellersPage = React.lazy(() => import('./pages/AdminSellersPage'));
const AdminOffersPage = React.lazy(() => import('./pages/AdminOffersPage'));
const AdminConfigPage = React.lazy(() => import('./pages/AdminConfigPage'));
const AdminRequestsPage = React.lazy(() => import('./pages/AdminRequestsPage'));
const AdminReportsPage = React.lazy(() => import('./pages/AdminReportsPage'));
const AdminDisputesPage = React.lazy(() => import('./pages/AdminDisputesPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/AdminAnalyticsPage'));

function App() {
  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/matches" element={<MatchesPage />} />
            <Route path="/matches/:id" element={<MatchDetailPage />} />
            <Route path="/auctions" element={<AuctionsPage />} />
            <Route path="/auctions/:id" element={<AuctionDetailPage />} />
            <Route path="/legal/terminos" element={<TermsPage />} />
            <Route path="/legal/privacidad" element={<PrivacyPage />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/legal/aviso" element={<LegalNoticePage />} />
            <Route path="/ayuda" element={<HelpPage />} />
            <Route path="/sobre-nosotros" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Auth Routes - Restricted for authenticated users */}
            <Route
              path="/login"
              element={
                <PublicRoute restricted={true}>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute restricted={true}>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute restricted={true}>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute restricted={true}>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests"
              element={
                <ProtectedRoute>
                  <RequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests/:id"
              element={
                <ProtectedRoute>
                  <RequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests/create"
              element={
                <ProtectedRoute>
                  <CreateRequestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute>
                  <OffersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ratings"
              element={
                <ProtectedRoute>
                  <RatingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity"
              element={
                <ProtectedRoute>
                  <ActivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <FavoritesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute requiredRole="BUYER">
                  <BuyerAccountPage />
                </ProtectedRoute>
              }
            />

            {/* Seller Routes */}
            <Route
              path="/seller/dashboard"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/seller/requests"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <SellerRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/offers"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <SellerOffersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/inventory"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <SellerInventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers/create"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <CreateOfferPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/stripe-setup"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <SellerStripeOnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Payment Routes */}
            <Route
              path="/checkout/auction/:auctionId"
              element={
                <ProtectedRoute>
                  <AuctionCheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/:offerId"
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/confirmation/:paymentId"
              element={
                <ProtectedRoute>
                  <PaymentConfirmationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <PaymentHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route path="/sellers/:id" element={<SellerPublicProfilePage />} />
            <Route
              path="/seller/apply"
              element={
                <ProtectedRoute>
                  <SellerApplyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller/sales"
              element={
                <ProtectedRoute requiredRole="SELLER">
                  <SellerSalesPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/matches"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminMatchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sellers"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminSellersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/offers"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminOffersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/requests"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/config"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/matches/create"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <CreateMatchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/disputes"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminDisputesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <AdminAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />
      <CookieConsent />

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
